import { User } from "@/@types";
import ChatService from "@/services/ChatService";
import webSocketService from "@/services/WebSocketService";
import { useMainStore } from "@/store";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import moment from "moment";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import ChatRoomContent from "./ChatRoomContent.vue";
import ChatRoomInput from "./ChatRoomInput.vue";

var channel;

export default defineComponent({
  props: {
    type: {
      type: String,
      default: "screen",
    },
  },
  setup() {
    const chat_service = new ChatService();

    const defaultMessage = ChatService.defaultMessage;

    const store = useMainStore();

    const event_bus = EventBus;

    let loadingChat = ref(false);
    let sectionItems = ref([] as any[]);
    let formatted_date = ref("");
    let editingMessageId = ref(null as number | null);
    let editMessageText = ref("");

    const id = computed(() => store?.chat_room_config?.id);
    const date = computed(() => store?.chat_room_config?.date);
    const user_name = computed(() => store?.chat_room_config?.user_name);
    const mode = computed(() => store?.chat_room_config?.mode);
    const session = computed((): User | null => store.session);

    onMounted(async () => {
      loadingChat.value = true;
      await getMessages();
    });

    watch([id, mode], async () => {
      if (id.value) {
        loadingChat.value = true;
        await getMessages();
        setupPusher(id.value);
      }
    });

    const getMessages = async () => {
      try {
        if (id.value && date.value) {
          loadingChat.value = true;
          setupPusher(id.value);

          sectionItems.value = await new ChatService().getMessages(
            id.value,
            date.value,
            sectionItems.value
          );

          formatted_date.value = moment(date.value).format("DD/MM/YYYY");

          event_bus.$emit(EventBusNames.UPDATE_CHATS);
          scrollToBottom();
        }
      } catch (error) {
        console.log(`ERRO NO GET ITEMS DA useChatMessages:`, error);
      } finally {
        loadingChat.value = false;
      }
    };

    const normalizeMessage = (message: any) => {
      const m = { ...message };
      if (!m.user_id && m.user?.id) m.user_id = m.user.id;
      if (!m.created_at && m.date) m.created_at = m.date;
      if (m.is_edited === undefined && m.edited_at !== undefined) {
        m.is_edited = m.edited_at !== null;
      }
      return m;
    };

    const setupPusher = (chatId: number) => {
      channel = webSocketService.subscribe(`appointment_chat.${chatId}`);
      channel.bind("new-message", (event: any) => {
        const msg = normalizeMessage(event.message);
        if (msg.user_id !== session.value?.id) {
          updateMessages(msg);
        }
      });
    };

    const updateMessages = async (message: any) => {
      sectionItems.value = await chat_service.updateMessages(
        normalizeMessage(message),
        sectionItems.value
      );
      event_bus.$emit(EventBusNames.UPDATE_CHATS);
      scrollToBottom();
    };

    const handleSend = async (msg: string) => {
      try {
        const res = await chat_service.sendMessage({
          appointment_chat_id: id.value,
          message: msg,
        });
        updateMessages(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    const canEditMessage = (item: any) => {
      const created = moment(item.created_at || item.date);
      return moment().diff(created, "minutes") < 15;
    };

    const startEdit = (item: any) => {
      editingMessageId.value = item.id;
      editMessageText.value = item.message;
    };

    const cancelEdit = () => {
      editingMessageId.value = null;
      editMessageText.value = "";
    };

    const submitEdit = async (item: any) => {
      if (!editMessageText.value.trim()) return;
      try {
        await chat_service.editMessage(item.id, editMessageText.value.trim());
        const target = sectionItems.value.find((s: any) => s.id === item.id);
        if (target) {
          target.message = editMessageText.value.trim();
          target.is_edited = true;
        }
        cancelEdit();
      } catch (e) {
        console.error("submitEdit", e);
      }
    };

    const scrollToBottom = () => {
      const chatCardBody = document.getElementById("chat_card_body");
      const chatModalBody = document.getElementById("chat_modal_body");
      setTimeout(() => {
        if (chatCardBody) chatCardBody.scrollTop = chatCardBody.scrollHeight;
        if (chatModalBody) chatModalBody.scrollTop = chatModalBody.scrollHeight;
      }, 100);
    };

    return {
      loadingChat,
      sectionItems,
      defaultMessage,
      user_name,
      formatted_date,
      getMessages,
      handleSend,
      chatId: id.value,
      session,
      editingMessageId,
      editMessageText,
      canEditMessage,
      startEdit,
      cancelEdit,
      submitEdit,
    };
  },
  beforeUnmount() {
    if (this.chatId) {
      webSocketService.unsubscribe(
        `appointment_chat.${this.chatId}`,
        "new-message"
      );
    }
  },
  components: {
    ChatRoomContent,
    ChatRoomInput,
  },
});
